import EventSchedule16 from '@carbon/icons-react/es/event--schedule/16';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import moment from 'moment';
import styled from 'styled-components';
import { DATE_INPUT_DISPLAY_FORMAT } from 'api/models';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';
import {
  ClickEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { useTranslation } from 'hooks/use-translation';
import { getBackgroundColor, NewInputBorder } from '../input/input';
import { DueDatePickerFooter } from './footer';
import { RangeType } from './types';

const NewUIRangePicker = styled(DatePicker.RangePicker)<RangePickerProps>`
  background-color: ${getBackgroundColor(true)};
  ${NewInputBorder};
  padding: var(--spacing-02) var(--spacing-05);
  height: var(--height-base);
  &.ant-picker-disabled {
    opacity: 0.5;
  }
`;

type DueDateRangePickerProps = Omit<
  RangePickerProps,
  | 'allowEmpty'
  | 'autoFocus'
  | 'bordered'
  | 'open'
  | 'placeholder'
  | 'suffixIcon'
  | 'picker'
> & {
  footerText?: string;
  endDateTextRange?: string;
  canChooseStartDate: boolean;
  disallowSingleDate?: boolean;
  hideFooter?: boolean;
  isTodayDisabled?: boolean;
  isTomorrowDisabled?: boolean;
  clearDates(): void;
  modifyPickerType(): void;
  pickToday(): void;
  pickTomorrow(): void;
  updateDateRange(values: RangeType): void;
  onDone(): void;
  hideModifyPicker?: boolean;
  hideClearDate?: boolean;
  disabledDate?(date: moment.Moment): boolean;
  dateRenderer?(
    currentDate: moment.Moment,
    today: moment.Moment,
  ): React.ReactNode;
  panelRender?: (originPanel: React.ReactNode) => React.ReactNode;
};

export const DueDateRangePicker = ({
  footerText,
  endDateTextRange,
  canChooseStartDate,
  disallowSingleDate,
  hideFooter,
  isTodayDisabled,
  isTomorrowDisabled,
  clearDates,
  modifyPickerType,
  pickToday,
  pickTomorrow,
  updateDateRange,
  hideModifyPicker,
  hideClearDate,
  onDone,
  disabledDate,
  dateRenderer,
  panelRender,
  ...rest
}: DueDateRangePickerProps) => {
  const startDateText = useTranslation('start_date');

  const defaultText = useTranslation('due_date');
  const dueDateText = endDateTextRange ?? defaultText;
  const isNewDesignEnabled = useAntdRlUISwitcher();
  const RangePickerToUse = isNewDesignEnabled
    ? NewUIRangePicker
    : DatePicker.RangePicker;

  useLoadLoggerForDataDog([ClickEventTags.DueDatePickerPopup]);

  return (
    <RangePickerToUse
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      format={DATE_INPUT_DISPLAY_FORMAT}
      allowEmpty={[true, true]}
      bordered={false}
      open
      placeholder={[startDateText, dueDateText]}
      tabIndex={-1}
      suffixIcon={<EventSchedule16 />}
      onChange={updateDateRange}
      disabledDate={disabledDate}
      dateRender={dateRenderer}
      panelRender={panelRender}
      renderExtraFooter={() => (
        <>
          {!hideFooter ? (
            <DueDatePickerFooter
              footerText={footerText}
              canChooseStartDate={canChooseStartDate}
              disallowSingleDate={disallowSingleDate}
              hideModifyPicker={hideModifyPicker}
              hideClearDate={hideClearDate}
              type="range"
              onPickToday={pickToday}
              onPickTomorrow={pickTomorrow}
              onClear={clearDates}
              modifyPickerType={modifyPickerType}
              onDone={onDone}
              isTodayDisabled={isTodayDisabled}
              isTomorrowDisabled={isTomorrowDisabled}
            />
          ) : (
            <></>
          )}
        </>
      )}
    />
  );
};
